import { useEffect, useId, useState, type FormEvent, type ReactElement } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';
import { TextField } from '@/components/ui/TextField';
import { useToast } from '@/components/ui/toast';
import { dataTypeLabel, fieldKeyFromLabel } from '@/components/documents/custom-field-format';

import { ENTITY_COPY, savedChanges } from '../copy';
import {
  DATA_TYPES,
  optionsError,
  parseOptions,
  parseReminder,
  REMINDER_ERROR,
} from '../custom-field-form';
import { catalogError, requiredTextError } from '../presentation';
import type { CustomFieldDefinition } from '../types';
import { useSaveCatalogItem } from '../use-catalogs-admin';
import { DateReminderFields } from './DateReminderFields';
import { Select } from '@/components/ui/Select';

const COPY = ENTITY_COPY.custom_field;

interface Form {
  label: string;
  dataType: string;
  options: string;
  required: boolean;
  /** Días de aviso de un campo de fecha, como texto; vacío = sin aviso. */
  reminder: string;
  /** Campo de fecha: avisar también cuando vence. */
  overdue: boolean;
}

/** Alta o edición de un campo propio (QA F1 #14). La clave interna sale del nombre;
 *  el tipo no se cambia después de crearlo y las opciones de una lista solo se
 *  amplían (las existentes quedan a la vista, fijas). Solo viaja lo que cambió. */
export function CustomFieldModal({
  open,
  editing,
  onClose,
}: {
  open: boolean;
  editing: CustomFieldDefinition | null;
  onClose: () => void;
}): ReactElement | null {
  const formId = useId();
  const typeId = useId();
  const optionsId = useId();
  const save = useSaveCatalogItem('custom-field-definitions');
  const { showToast } = useToast();
  const [form, setForm] = useState<Form>({
    label: '',
    dataType: 'text',
    options: '',
    required: false,
    reminder: '',
    overdue: false,
  });
  const [checked, setChecked] = useState(false);
  const [server, setServer] = useState<ReturnType<typeof catalogError> | null>(null);
  const existing = editing?.options ?? [];
  const isSelect = form.dataType === 'select';
  const isDate = form.dataType === 'date';
  const reminder = isDate ? parseReminder(form.reminder) : null;

  useEffect(() => {
    if (open) {
      setForm({
        label: editing?.label ?? '',
        dataType: editing?.dataType ?? 'text',
        options: '',
        required: editing?.required ?? false,
        reminder:
          editing === null || editing.reminderDays === null ? '' : String(editing.reminderDays),
        overdue: editing?.notifyOverdue ?? false,
      });
      setChecked(false);
      setServer(null);
    }
  }, [open, editing]);

  const set = (patch: Partial<Form>): void => {
    setForm((f) => ({ ...f, ...patch }));
    setServer(null);
  };
  const labelError = (): string | null => {
    const base = requiredTextError(form.label, 200, 'Escribe el nombre del campo');
    if (base !== null) {
      return base;
    }
    return fieldKeyFromLabel(form.label) === '' ? 'Usa letras o números en el nombre' : null;
  };
  const optsError = (): string | null =>
    isSelect ? optionsError(parseOptions(form.options), existing, editing === null) : null;
  const labelMsg =
    server !== null && server.field !== null ? server.message : checked ? labelError() : null;
  const optsMsg = checked ? optsError() : null;
  const reminderMsg = checked && reminder === undefined ? REMINDER_ERROR : null;

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setChecked(true);
    if (labelError() !== null || optsError() !== null || reminder === undefined || save.isPending) {
      return;
    }
    const label = form.label.trim();
    const added = isSelect ? parseOptions(form.options) : [];
    const body: Record<string, unknown> =
      editing === null
        ? {
            fieldKey: fieldKeyFromLabel(label),
            label,
            dataType: form.dataType,
            ...(isSelect ? { options: added } : {}),
            ...(isDate && reminder !== null ? { reminderDays: reminder } : {}),
            ...(isDate && form.overdue ? { notifyOverdue: true } : {}),
            required: form.required,
          }
        : {
            ...(label !== editing.label ? { label } : {}),
            ...(form.required !== editing.required ? { required: form.required } : {}),
            ...(added.length > 0 ? { options: [...existing, ...added] } : {}),
            ...(isDate && reminder !== editing.reminderDays ? { reminderDays: reminder } : {}),
            ...(isDate && form.overdue !== editing.notifyOverdue
              ? { notifyOverdue: form.overdue }
              : {}),
          };
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    try {
      await save.mutateAsync({ id: editing?.id ?? null, body });
      showToast(editing === null ? COPY.created(label) : savedChanges(label));
      onClose();
    } catch (err) {
      setServer(catalogError(err, 'custom_field'));
    }
  }

  const newlyRequired = editing !== null && !editing.required && form.required;
  return (
    <ModalShell
      open={open}
      title={editing === null ? COPY.newTitle : COPY.editTitle}
      busy={save.isPending}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn mut sm" onClick={onClose} disabled={save.isPending}>
            Cancelar
          </button>
          <button type="submit" form={formId} className="btn sm" disabled={save.isPending}>
            {save.isPending
              ? 'Guardando…'
              : editing === null
                ? COPY.createLabel
                : 'Guardar cambios'}
          </button>
        </>
      }
    >
      <form id={formId} className="form-grid" noValidate onSubmit={(e) => void submit(e)}>
        <TextField
          label="Nombre del campo"
          value={form.label}
          maxLength={200}
          placeholder="Ubicación"
          error={labelMsg}
          onChange={(label) => set({ label })}
        />
        <div className="fld">
          <label htmlFor={typeId} className="fld-lab">
            Tipo de dato
          </label>
          <Select
            id={typeId}
            value={form.dataType}
            disabled={editing !== null}
            options={DATA_TYPES.map((t) => ({ value: t, label: dataTypeLabel(t) }))}
            onChange={(v) => set({ dataType: v })}
          />
        </div>
        {isSelect && (
          <div className="fld">
            {existing.length > 0 && (
              <p className="fld-hint">
                Opciones actuales: <span>{existing.join(' · ')}</span>
              </p>
            )}
            <label htmlFor={optionsId} className="fld-lab">
              {editing === null ? 'Opciones' : 'Opciones nuevas'}
            </label>
            <textarea
              id={optionsId}
              value={form.options}
              rows={4}
              placeholder="Una opción por línea"
              aria-invalid={optsMsg !== null}
              aria-describedby={optsMsg === null ? `${optionsId}-hint` : `${optionsId}-err`}
              onChange={(e) => set({ options: e.target.value })}
              style={{ width: '100%' }}
            />
            {optsMsg !== null ? (
              <span id={`${optionsId}-err`} className="fld-err">
                {optsMsg}
              </span>
            ) : (
              <span id={`${optionsId}-hint`} className="fld-hint">
                Una por línea. Después solo se pueden agregar, no quitar.
              </span>
            )}
          </div>
        )}
        {isDate && (
          <DateReminderFields
            days={form.reminder}
            overdue={form.overdue}
            error={reminderMsg}
            onDaysChange={(v) => set({ reminder: v })}
            onOverdueChange={(overdue) => set({ overdue })}
          />
        )}
        <label className="chk">
          <input
            type="checkbox"
            checked={form.required}
            onChange={(e) => set({ required: e.target.checked })}
          />
          Obligatorio
        </label>
        {newlyRequired && (
          <p className="fld-hint">
            Los documentos que no lo tengan lo mostrarán como pendiente hasta completarlo.
          </p>
        )}
      </form>
    </ModalShell>
  );
}
